import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React from 'react';
import { Accordion, Box, Copy, Optional, Link, Icon, IconWrapper, H3 } from '@honda-canada/design-system-react';
import { useTranslation } from 'react-i18next';
import { usePublicationsAssetsContext, useLanguage } from '../../utils/sitecoreContext';
import ErrorUI from './ErrorUI';
import Loader from '../../utils/components/Loader';
import { getHtmlLinks, getPdfLink } from './utils';
import themeStyles from './styles/OwnerManuals.styles';
const DownloadLinkContainer = themeStyles.apply(Box, 'DownloadLinkContainer');
const TitleContainer = themeStyles.apply(Box, 'TitleContainer');
const Container = themeStyles.apply(Box, 'Container');
const marginTopByDepth = {
    3: ['s', 's', '0'],
    4: ['s', 's', '0'],
    default: ['s', 's', 'l'],
};
const paddingLeftByDepth = {
    2: 'xxs',
    default: '0',
};
const getDownloadFileLink = (prefixes, filePath) => `${prefixes.join('')}${filePath}`;
const DownLoadLink = ({ downloadFilePrefix, item, depth }) => {
    const language = useLanguage();
    let containerStyles;
    switch (depth) {
        case 2:
            containerStyles = { pl: ['l', 'm', 'l'], pr: 'm' };
            break;
        case 3:
            containerStyles = { pl: ['l', 'l', 'xl'], pr: 'm' };
            break;
        case 4:
            containerStyles = { pl: ['m', 'm', 'l'], pr: 'm' };
            break;
        default:
            containerStyles = { pl: 'default', pr: 'xs' };
            break;
    }
    return (_jsxs(DownloadLinkContainer, { styles: containerStyles, children: [_jsx(IconWrapper, { children: _jsx(Icon, { name: "save", iconColor: "blue" }) }), _jsx(Link, { styling: "tertiary2", href: getDownloadFileLink([downloadFilePrefix], language === 'en' ? item === null || item === void 0 ? void 0 : item.fileEn : item === null || item === void 0 ? void 0 : item.fileFr), target: "_blank", children: language === 'en' ? item === null || item === void 0 ? void 0 : item.labelEn : item === null || item === void 0 ? void 0 : item.labelFr })] }));
};
const sortBySectionNumber = (item) => item.sort((a, b) => parseInt(a.sectionNumber, 10) - parseInt(b.sectionNumber, 10));
const NestedSection = ({ items, depth }) => {
    var _a, _b, _c;
    const downloadFilePrefix = (_a = usePublicationsAssetsContext()) !== null && _a !== void 0 ? _a : '';
    const language = useLanguage();
    let contentContainerStyles;
    let panelBackgroundColor;
    let itemsHaveBorders;
    let accordionTitleFont = '';
    let fontFamily = 'bold';
    switch (depth) {
        case 2:
            contentContainerStyles = {
                py: '0 !important',
                ml: 'm',
                mb: 'm',
                mr: ['l', 'l', 'big'],
                // Issue: Overriding existing styles in DSR using props is not working as expected
                // and Specificity issues are preventing the new styles from being applied.
                // Solution: As a wrokaround, we need to use !important to ensure the new styles take precedence.
                width: 'auto !important',
            };
            panelBackgroundColor = 'paleBlue';
            itemsHaveBorders = false;
            accordionTitleFont = 'payment';
            break;
        case 3:
            contentContainerStyles = { py: '0', pl: ['l', 'l', 'xl'], width: 'auto' };
            itemsHaveBorders = true;
            accordionTitleFont = 'regular';
            break;
        case 4:
            contentContainerStyles = { py: '0', pl: ['m', 'm', 'l'], mr: ['l', 'l', 'big'], width: 'auto' };
            itemsHaveBorders = true;
            accordionTitleFont = 'regular';
            fontFamily = 'regular';
            break;
        default:
            contentContainerStyles = { py: 's' };
            itemsHaveBorders = true;
            accordionTitleFont = 'accordionLarge';
            break;
    }
    const [withChildren, withoutChildren] = items.reduce((acc, curr) => {
        if (curr.children.length) {
            acc[0].push(curr);
        }
        else {
            acc[1].push(curr);
        }
        return acc;
    }, [[], []]);
    const itemsWithChildren = sortBySectionNumber(withChildren).map(item => {
        const titlePadding = depth === 1 || depth === 2 ? ['m', 'm', 'default'] : 'none';
        return {
            key: item.sectionId,
            title: (_jsx(Copy, { pl: titlePadding, size: accordionTitleFont, textAlign: "left", fontFamily: fontFamily, "data-testId": "accordion-title", children: language === 'en' ? item.labelEn : item.labelFr })),
            // Recursively render children
            content: _jsx(NestedSection, { items: item.children, depth: depth + 1 }),
        };
    });
    return (_jsxs(_Fragment, { children: [_jsx(Optional, { when: itemsWithChildren.length, children: _jsx(Box, { pl: (_b = paddingLeftByDepth[depth]) !== null && _b !== void 0 ? _b : paddingLeftByDepth.default, mt: (_c = marginTopByDepth[depth]) !== null && _c !== void 0 ? _c : marginTopByDepth.default, children: _jsx(Accordion, { items: itemsWithChildren, iconName: "animatedArrowUpDown", contentContainerStyles: contentContainerStyles, panelBackgroundColor: panelBackgroundColor, itemsHaveBorders: itemsHaveBorders, hideBorderOnLastItem: false }) }) }), sortBySectionNumber(withoutChildren).map(item => (_jsx(Box, { children: _jsx(DownLoadLink, { downloadFilePrefix: downloadFilePrefix, item: item, depth: depth }) }, item.sectionId)))] }));
};
const DownloadsAvailable = ({ downloads, isError, isFetching }) => {
    var _a;
    const downloadFilePrefix = (_a = usePublicationsAssetsContext()) !== null && _a !== void 0 ? _a : '';
    const { t } = useTranslation();
    const language = useLanguage();
    if (isError) {
        return (_jsxs(Container, { children: [_jsx(TitleContainer, { children: _jsx(H3, { children: t('Pages.Owners.Manuals.downloadsAvailableLabel') }) }), _jsx(ErrorUI, { message: t('Pages.Owners.Manuals.apiLoadingError') })] }));
    }
    if (isFetching) {
        return (_jsxs(Container, { children: [_jsx(TitleContainer, { children: _jsx(H3, { children: t('Pages.Owners.Manuals.downloadsAvailableLabel') }) }), _jsx(Loader, { testId: "cy-manuals-loader" })] }));
    }
    if (!downloads || !Array.isArray(downloads))
        return null;
    return (_jsxs(_Fragment, { children: [_jsx(TitleContainer, { children: _jsx(H3, { "data-testid": "cy-downloads-title", children: t('Pages.Owners.Manuals.downloadsAvailableLabel') }) }), downloads.map(download => {
                var _a;
                if (download.type === 'downloadLink') {
                    const items = download === null || download === void 0 ? void 0 : download.items;
                    const hrefLink = ((_a = download === null || download === void 0 ? void 0 : download.items) === null || _a === void 0 ? void 0 : _a.html)
                        ? getHtmlLinks({ language, items, downloadFilePrefix })
                        : getPdfLink({ language, items, downloadFilePrefix });
                    return (_jsxs(DownloadLinkContainer, { styles: {
                            pl: ['xs', 'xs', 'big'],
                        }, children: [_jsx(IconWrapper, { children: _jsx(Icon, { name: "save", iconColor: "blue" }) }), _jsx(Link, { "data-testid": "cy-navigation-manual-item", styling: "tertiary2", href: hrefLink, target: "_blank", children: language === 'en' ? download.items.labelEn : download.items.labelFr })] }, download.publicationId));
                }
                return _jsx(NestedSection, { items: download.items, depth: 1 }, download.publicationId);
            })] }));
};
export default DownloadsAvailable;
